import { apiClient } from "@lib/axios";
import { environment } from "@environment";
import type {
  EnqueuePresentationGenerationRequest,
  EnqueuePresentationGenerationResponse,
  PresentationGenerationJobStatus,
  PresentationGenerationPartial,
  PresentationGenerationResultResponse,
  RegenerateSlideResponse,
  RegenerationStatus,
} from "./types";

// Absolute URL: generation runs on its own Cloud Run service (it needs
// poppler-utils, unavailable on App Engine). Axios ignores baseURL for
// absolute URLs; the auth interceptors still apply.
const path = (path = "") => `${environment.generationBackend}${path}`;
export const presentationGenerationApi = {
  enqueueGeneration: (
    data: EnqueuePresentationGenerationRequest,
    signal?: AbortSignal,
  ) => {
    if (signal) {
      return apiClient.post<EnqueuePresentationGenerationResponse>(
        path(),
        data,
        { signal },
      );
    }

    return apiClient.post<EnqueuePresentationGenerationResponse>(path(), data);
  },

  // `since` is how many streamed slides the caller has already taken. Slides are
  // large, so the backend sends only the ones past that mark rather than the
  // whole deck on every poll.
  getJobStatus: (jobId: string, signal?: AbortSignal, since?: number) => {
    const url = since ? path(`/${jobId}?since=${since}`) : path(`/${jobId}`);
    if (signal) {
      return apiClient.get<PresentationGenerationJobStatus>(url, { signal });
    }

    return apiClient.get<PresentationGenerationJobStatus>(url);
  },

  getJobPartial: (jobId: string, signal?: AbortSignal) => {
    if (signal) {
      return apiClient.get<PresentationGenerationPartial>(
        path(`/${jobId}/partial`),
        { signal },
      );
    }
    return apiClient.get<PresentationGenerationPartial>(
      path(`/${jobId}/partial`),
    );
  },
  getJobResult: (jobId: string, signal?: AbortSignal) => {
    if (signal) {
      return apiClient.get<PresentationGenerationResultResponse>(
        path(`/${jobId}/result`),
        { signal },
      );
    }

    return apiClient.get<PresentationGenerationResultResponse>(
      path(`/${jobId}/result`),
    );
  },

  regenerateSlide: (jobId: string, pageNumber: number, signal?: AbortSignal) =>
    apiClient.post<RegenerateSlideResponse>(
      path(`/${jobId}/pages/${pageNumber}/regenerate`),
      {},
      signal ? { signal } : undefined,
    ),

  getRegeneration: (
    jobId: string,
    pageNumber: number,
    regenerationId: string,
    signal?: AbortSignal,
  ) =>
    apiClient.get<RegenerationStatus>(
      path(`/${jobId}/pages/${pageNumber}/regenerate/${regenerationId}`),
      signal ? { signal } : undefined,
    ),
};
